// swimlaneRows — app-shell-local, walk-derived row helpers for the swimlane
// surface that have no scene peer: the comment / first-real-step predicates,
// the flow-graph node-key map, the trigger/wait row-index sets, the live
// row-band pixel accessor (`RowGeometry` / `blockYBounds`), and the aux-edge
// INPUT resolvers. The flow-node predicate and forward control-flow chain moved
// to scene's `swimlaneTopology`, and all edge GEOMETRY to scene's
// `swimlane-edges` (one source of truth, shared with the export composer).
//
// What lives here is the irreducible LIVE glue that has no scene peer: mapping
// the walk's saga/`go` semantics + the flow-graph's node keys onto the
// row-index `SwimlaneAuxEdge[]` scene's `swimlaneFlowAux` / `swimlaneSeqArcs`
// consume, plus the `from`-key sidecar the re-entry-comment de-dup needs. It
// PRODUCES scene inputs and re-keys identity; it never recomputes an edge `d`.
// Pure: functions of the walk + layout rows + scene geometry, nothing
// DOM-measured.

import type { Row, SwimlaneLayout, WalkResult } from "@archlang/engine";
import { isAwaitNodeId } from "@archlang/engine";
import { LANE_HEADER_H, ROW_MIN_H, type SwimlaneAuxEdge } from "@archlang/scene/process";
import type { FlowGraph } from "./flow-graph.js";

/** An ownerless note row (a comment) — annotation, not a flow action. */
export function isCommentRow(row: Row, walk: WalkResult | null): boolean {
  if (row.stepIndex === null) return false;
  const step = walk?.byIndex.get(row.stepIndex);
  if (!step) return false;
  return step.noteText !== undefined && step.from.length === 0;
}

/** True when stepIndex is the first non-comment step. Comments may appear
 *  before the first real action; their stepIndex 0 should not draw an entry
 *  dot. Compares by stable index (the selected walk's indices have gaps). */
export function isFirstRealStep(stepIndex: number, walk: WalkResult | null): boolean {
  if (!walk) return stepIndex === 0;
  for (const s of walk.steps) {
    if (s.noteText !== undefined && s.from.length === 0) continue;
    return s.stepIndex === stepIndex;
  }
  return false;
}

/** Flow-graph node key → its Row (steps `s<stepIndex>`, terminator / do-header
 *  rows `r<layout-row idx>` — the keys `flowGraph`/`nodeKeyByNodeId` use), so
 *  the aux-edge pass can resolve a drop/comp/reentry endpoint back to geometry. */
export function computeRowByNodeKey(layout: SwimlaneLayout): Map<string, Row> {
  const m = new Map<string, Row>();
  layout.rows.forEach((r, idx) => {
    if (r.stepIndex != null) m.set(`s${r.stepIndex}`, r);
    if (r.terminator || r.subprocessHeader) m.set(`r${idx}`, r);
  });
  return m;
}

/** Row indexes that are START triggers (a leading inbound `await`) — lifted to a
 *  set so the geometry pass can budget the `▶` marker into line 0's wrap. */
export function computeTriggerRowIndexes(layout: SwimlaneLayout, walkResult: WalkResult | null): Set<number> {
  const s = new Set<number>();
  const trig = walkResult?.triggers;
  if (!trig || trig.length === 0) return s;
  layout.rows.forEach((r, i) => {
    if (r.stepIndex === null) return;
    const step = walkResult?.byIndex.get(r.stepIndex);
    if (step && trig.includes(step.nodeId)) s.add(i);
  });
  return s;
}

/** Row indexes that are MID-FLOW `await` waits — an `await` step that is NOT a
 *  leading start trigger (PROCESS.md §6); rendered with a timer/clock marker. */
export function computeWaitRowIndexes(layout: SwimlaneLayout, walkResult: WalkResult | null): Set<number> {
  const s = new Set<number>();
  const trig = new Set(walkResult?.triggers ?? []);
  layout.rows.forEach((r, i) => {
    if (r.stepIndex === null) return;
    const step = walkResult?.byIndex.get(r.stepIndex);
    if (!step || trig.has(step.nodeId)) return;
    if (isAwaitNodeId(step.nodeId)) s.add(i);
  });
  return s;
}

// ─────────────────────────────────────────────────────────────────────────────
// Live row-band pixel accessor — the grid-relative view of scene's geometry the
// overlay renderers (swimlaneArrows / swimlaneFrames) and the comment/latency
// label passes consume. Derived from `swimlaneGeometry` (scene) — never
// DOM-measured. Kept app-shell-local because it is a canvas-coordinate accessor
// (grid-relative geom + `LANE_HEADER_H`), not shared edge math.
// ─────────────────────────────────────────────────────────────────────────────

/** Grid-relative pixel geometry the overlay consumes — row tops/heights plus
 *  each block's measured top/bottom keyed `${rowY}:${laneIndex}`. */
export interface RowGeometry {
  readonly tops: readonly number[];
  readonly heights: readonly number[];
  readonly total: number;
  readonly blocks: ReadonlyMap<string, { readonly top: number; readonly bottom: number }>;
}

/** Real top/bottom/centre Y of a block — its measured box when the cell
 *  registered one, else the padded row band. Lane −1 = a diamond row (no
 *  block); it falls through to the row band, which is what the diamonds want. */
export function blockYBounds(
  y: number,
  laneIdx: number,
  geom: RowGeometry,
  topPad: number,
  bottomPad: number,
) {
  const measured = geom.blocks.get(`${y}:${laneIdx}`);
  if (measured) {
    const blockTop = LANE_HEADER_H + measured.top;
    const blockBottom = LANE_HEADER_H + measured.bottom;
    return { blockTop, blockBottom, blockCy: (blockTop + blockBottom) / 2 };
  }
  const top = LANE_HEADER_H + (geom.tops[y] ?? 0);
  const h = geom.heights[y] ?? ROW_MIN_H;
  const blockTop = top + topPad;
  const blockBottom = top + h - bottomPad;
  const blockCy = (blockTop + blockBottom) / 2;
  return { blockTop, blockBottom, blockCy };
}

// ─────────────────────────────────────────────────────────────────────────────
// Aux-edge INPUT resolvers — map the walk's saga/`go` semantics + the
// flow-graph's node keys onto the row-index `SwimlaneAuxEdge[]` scene's
// `swimlaneFlowAux` / `swimlaneSeqArcs` consume. Each returns the resolved
// edges plus a `from`-key sidecar (`aux path id → source flow-graph node key`)
// for the re-entry-comment de-dup — the one bit of identity the geometric arc
// can't carry. No geometry: the `d` is scene's.
// ─────────────────────────────────────────────────────────────────────────────

/** Resolved aux edges + their `from`-key sidecar, keyed by the id scene echoes
 *  back on the arc (so a keyed caller can recover the source node key). */
export interface ResolvedAuxEdges {
  readonly edges: SwimlaneAuxEdge[];
  /** Arc id (as scene emits it) → source flow-graph node key (`s<idx>`). */
  readonly fromById: ReadonlyMap<string, string>;
}

/** Flow-variant aux edges (saga drop / reverse compensation chain / `go`
 *  re-entry) from the flow graph. Endpoints (flow-graph node keys) resolve to
 *  layout-row-index pairs — the shape `swimlaneFlowAux` takes. `fromById` is
 *  keyed by scene's emitted `aux:<id>` so the re-entry comment de-dup can map an
 *  arc back to its `go` step. */
export function resolveFlowAuxEdges(
  layout: SwimlaneLayout,
  flowGraph: FlowGraph,
  rowByNodeKey: ReadonlyMap<string, Row>,
): ResolvedAuxEdges {
  const rowIndexOf = new Map(layout.rows.map((r, i) => [r, i] as const));
  const edges: SwimlaneAuxEdge[] = [];
  const fromById = new Map<string, string>();
  for (const e of flowGraph.auxEdges) {
    const fromRow = rowByNodeKey.get(e.from);
    const toRow = rowByNodeKey.get(e.to);
    if (!fromRow || !toRow) continue;
    const fi = rowIndexOf.get(fromRow), ti = rowIndexOf.get(toRow);
    if (fi === undefined || ti === undefined) continue;
    fromById.set(`aux:${e.id}`, e.from);
    edges.push({ id: e.id, kind: e.kind, fromRowIndex: fi, toRowIndex: ti, ...(e.comment ? { comment: e.comment } : {}) });
  }
  return { edges, fromById };
}

/** Sequence `go` re-entry edges (PROCESS.md §7) from the walk's `reentries`.
 *  Drops an edge whose target is unresolved, either endpoint row is missing, or
 *  either sits in a hidden band. `fromById` maps the arc id → `s<fromIdx>`. */
export function resolveSeqReentryEdges(
  layout: SwimlaneLayout,
  walk: WalkResult | null,
  hiddenRows: ReadonlySet<Row>,
): ResolvedAuxEdges {
  const edges: SwimlaneAuxEdge[] = [];
  const fromById = new Map<string, string>();
  if (!walk) return { edges, fromById };
  const stepIndexByNodeId = new Map<string, number>();
  for (const s of walk.steps) stepIndexByNodeId.set(s.nodeId, s.stepIndex);
  const rowByStepIndex = new Map<number, Row>();
  const rowIndexOf = new Map<Row, number>();
  layout.rows.forEach((r, i) => {
    rowIndexOf.set(r, i);
    if (r.stepIndex !== null) rowByStepIndex.set(r.stepIndex, r);
  });
  for (const re of walk.reentries ?? []) {
    if (!re.toNodeId) continue;
    const fromIdx = stepIndexByNodeId.get(re.fromNodeId);
    const toIdx = stepIndexByNodeId.get(re.toNodeId);
    if (fromIdx === undefined || toIdx === undefined) continue;
    const fromRow = rowByStepIndex.get(fromIdx);
    const toRow = rowByStepIndex.get(toIdx);
    if (!fromRow || !toRow) continue;
    if (hiddenRows.has(fromRow) || hiddenRows.has(toRow)) continue;
    const fi = rowIndexOf.get(fromRow), ti = rowIndexOf.get(toRow);
    if (fi === undefined || ti === undefined) continue;
    const step = walk.byIndex.get(fromIdx);
    const id = `seq-re-${fromIdx}`;
    edges.push({ id, kind: "reentry", fromRowIndex: fi, toRowIndex: ti, ...(step?.comment ? { comment: step.comment } : {}) });
    fromById.set(id, `s${fromIdx}`);
  }
  return { edges, fromById };
}

/** Sequence saga edges (PROCESS.md §8) from the walk's `compensation`: the
 *  reverse `comp` chain + each forward step's failure `drop`. No `fromById` —
 *  these arcs carry no comment. Endpoints resolve to layout rows (steps by
 *  index, terminators by nodeId). */
export function resolveSeqCompEdges(
  layout: SwimlaneLayout,
  walk: WalkResult | null,
  hiddenRows: ReadonlySet<Row>,
): SwimlaneAuxEdge[] {
  const edges: SwimlaneAuxEdge[] = [];
  if (!walk) return edges;
  const rowByStepIndex = new Map<number, Row>();
  const rowByNodeId = new Map<string, Row>();
  const rowIndexOf = new Map<Row, number>();
  layout.rows.forEach((r, i) => {
    rowIndexOf.set(r, i);
    if (r.stepIndex !== null) rowByStepIndex.set(r.stepIndex, r);
    if (r.terminator) rowByNodeId.set(r.terminator.nodeId, r);
  });
  const stepIndexByNodeId = new Map<string, number>();
  for (const s of walk.steps) stepIndexByNodeId.set(s.nodeId, s.stepIndex);
  const rowOf = (nodeId: string): Row | undefined => {
    const si = stepIndexByNodeId.get(nodeId);
    return si !== undefined ? rowByStepIndex.get(si) : rowByNodeId.get(nodeId);
  };
  const add = (kind: "comp" | "drop", fromId: string, toId: string, key: string): void => {
    const fromRow = rowOf(fromId);
    const toRow = rowOf(toId);
    if (!fromRow || !toRow) return;
    if (hiddenRows.has(fromRow) || hiddenRows.has(toRow)) return;
    const fi = rowIndexOf.get(fromRow), ti = rowIndexOf.get(toRow);
    if (fi === undefined || ti === undefined) return;
    edges.push({ id: key, kind, fromRowIndex: fi, toRowIndex: ti });
  };
  for (const f of walk.compensation ?? []) {
    for (let i = 0; i + 1 < f.chain.length; i++) add("comp", f.chain[i]!, f.chain[i + 1]!, `seq-comp-${f.reversibleId}-${i}`);
    for (const dr of f.drops) add("drop", dr.fromNodeId, dr.toNodeId, `seq-drop-${dr.fromNodeId}`);
  }
  return edges;
}
